import _ from "lodash";
import React, { useCallback, useMemo, useReducer, useState } from "react";
import moment from "moment-timezone";
import CustomTimePickerModal from "./CustomTimePickerModal";
import { getUserActivityReport } from "../../../../services/userActivities";
import { downloadReferralLetter } from "../../../../services/referralLetters";
import { STATUS, TIME_FORMAT } from "../../../../constants";

const { DATE_WITH_DASH } = TIME_FORMAT;
const { DEFAULT, LOADING, SUCCEED, FAILED } = STATUS;

const customTimePickerReducer = (state, action) => {
  switch (action.type) {
    case "updateStartTime":
      return { ...state, startTime: action.time };
    case "updateEndTime":
      return { ...state, endTime: action.time };
    case "toggleModal":
      return { ...state, isModalOpen: action.isOpen };
    case "updateDateRange":
      return { ...state, startTime: action.startTime, endTime: action.endTime };
    case "toggleDropDown":
      return { ...state, isDropDownOpen: action.isOpen };
    case "selectTimeRange":
      return { ...state, rangeType: action.rangeType, isDropDownOpen: action.isDropDownOpen };
    case "selectCustomTime":
      return { ...state, isDropDownOpen: action.isDropDownOpen, isModalOpen: action.isModalOpen };
    default:
      return state;
  }
};

const customTimePickerInitialState = {
  startTime: null,
  endTime: null,
  isModalOpen: false,
  isDropDownOpen: false,
  rangeType: "",
};

const TimePicker = () => {
  const [customTimePickerState, dispatch] = useReducer(customTimePickerReducer, customTimePickerInitialState);
  const [downloadStatus, setDownloadStatus] = useState(DEFAULT);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSelectCustomRange = useCallback(() => {
    dispatch({ type: "selectCustomTime", isModalOpen: true, isDropDownOpen: false });
  }, [dispatch]);

  const generateDateRange = useCallback(
    (rangeType) => {
      dispatch({ type: "selectTimeRange", rangeType, isDropDownOpen: false });
      const now = moment();
      if (rangeType === "1week") {
        const oneWeekBefore = now.clone().subtract(1, "week").add(1, "day");
        return dispatch({
          type: "updateDateRange",
          startTime: oneWeekBefore,
          endTime: now,
        });
      }

      if (rangeType === "1month") {
        const oneMonthBefore = now.clone().subtract(1, "month").add(1, "day");
        return dispatch({
          type: "updateDateRange",
          startTime: oneMonthBefore,
          endTime: now,
        });
      }
    },
    [dispatch]
  );

  const DROPDOWN_LIST_MAP = useMemo(() => {
    return [
      {
        label: "1 Week",
        value: "1week",
        handleClick: () => generateDateRange("1week"),
      },
      {
        label: "1 Month",
        value: "1month",
        handleClick: () => generateDateRange("1month"),
      },
      {
        label: (() => {
          if (customTimePickerState.rangeType !== "custom") {
            return "Custom";
          }
          return _.isNull(customTimePickerState.startTime) || _.isNull(customTimePickerState.endTime)
            ? "Custom"
            : `Custom(${customTimePickerState.startTime.format("DD/MM/YYYY")} to ${customTimePickerState.endTime.format(
                "DD/MM/YYYY"
              )})`;
        })(),
        value: "custom",
        handleClick: handleSelectCustomRange,
      },
    ];
  }, [
    generateDateRange,
    handleSelectCustomRange,
    customTimePickerState.startTime,
    customTimePickerState.endTime,
    customTimePickerState.rangeType,
  ]);

  const handleDownloadReport = useCallback(async () => {
    setErrorMessage(null);
    setDownloadStatus(LOADING);
    const { startTime, endTime } = customTimePickerState;
    if (_.isEmpty(startTime) || _.isEmpty(endTime)) {
      setDownloadStatus(FAILED);
      setErrorMessage("You must select a valid time range.");
      return;
    }
    const { data } = await getUserActivityReport({
      startDate: moment(startTime).format(DATE_WITH_DASH),
      endDate: moment(endTime).format(DATE_WITH_DASH),
    });
    if (data) {
      setDownloadStatus(SUCCEED);
      downloadReferralLetter([data], "user-activity-report.csv");
      return;
    }
    setDownloadStatus(FAILED);
  }, [setDownloadStatus, customTimePickerState]);

  const generateDropdownLabel = useCallback(() => {
    const defaultLabel = "Report Time Picker";
    const { rangeType } = customTimePickerState;
    if (rangeType) {
      const item = DROPDOWN_LIST_MAP.find(({ value }) => value === rangeType);
      return item?.label || defaultLabel;
    }

    return defaultLabel;
  }, [customTimePickerState, DROPDOWN_LIST_MAP]);

  return (
    <div>
      <div className="is-flex">
        <div className={`dropdown is-flex-grow-2${customTimePickerState.isDropDownOpen ? " is-active" : ""}`}>
          <div className="dropdown-trigger">
            <button
              className="button is-radiusless is-small"
              aria-haspopup="true"
              aria-controls="dropdown-menu"
              onClick={() => dispatch({ type: "toggleDropDown", isOpen: true })}
            >
              <span>{generateDropdownLabel()}</span>
              <span className="icon is-small">
                <i className="fas fa-angle-down" aria-hidden="true" />
              </span>
            </button>
          </div>
          <div className="dropdown-menu" id="dropdown-menu" role="menu">
            <ul className="dropdown-content" style={{ cursor: "pointer" }}>
              {DROPDOWN_LIST_MAP.map(({ label, value, handleClick }) => (
                <li
                  key={value}
                  className={`dropdown-item${value === customTimePickerState.rangeType ? " is-active" : ""}`}
                  onClick={handleClick}
                >
                  <p className="is-size-7">{label}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <button
          className={`button is-primary ml-2 is-flex-grow-1${
            downloadStatus === LOADING ? " is-loading" : ""
          } is-radiusless is-small`}
          onClick={handleDownloadReport}
        >
          Download Report
        </button>
      </div>

      {!_.isEmpty(errorMessage) && <p className="is-size-7 has-text-danger">{errorMessage}</p>}

      <CustomTimePickerModal isModalOpen={customTimePickerState.isModalOpen} dispatch={dispatch} />
    </div>
  );
};

export default TimePicker;
